"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import { setPageTitle } from "@/lib/page-titles";
import { LandingFooter } from "@/components/ui/footer";
import { PillButton } from "@/components/ui/pill-button";
import { cn } from "@/lib/utils";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { DecodeText } from "@/components/ui/decode-text";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import { ContentLockup } from "@/components/ui/content-lockup";
import { ExpandingFrames } from "@/components/ui/expanding-frames";
import dynamic from "next/dynamic";

// Dynamically import parametric components to avoid SSR issues with Three.js
const ArcsLinesDots = dynamic(
  () => import("@/components/parametric/ArcsLinesDots"),
  { ssr: false }
);

export default function ResearchPage() {
  useEffect(() => {
    setPageTitle("Research");
  }, []);

  return (
    <main className="relative min-h-screen bg-[#FFFDF0] text-[#0a0a0a] overflow-x-clip">
      <div className="pointer-events-none absolute right-0 top-0 h-[912px] left-0 overflow-hidden opacity-80">
        <img
          src="/images/landing/research.svg"
          alt="Research Landing Background"
          className="w-full h-full object-cover"
        />
        {/* Gradient overlay to fade out at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[450px] z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255, 253, 240, 1) 0%, rgba(255, 253, 240, 0) 100%)",
          }}
        />
      </div>
      <SiteNavigation isDark={false} activePage="research" />
      <div className="relative mx-auto max-w-[96rem]">
        {/* Parametric animation background */}
        <NewHero />
      </div>
      <BeyondPatternMatchingSection />
      <OurThesisSection />
      <OurTeamSection />
      <LandingFooter
        colorMode="gray"
        className="max-w-[96rem] lg:max-w-[96rem] xl:max-w-[96rem] px-8 sm:px-8 lg:px-24 xl:px-24"
      />
    </main>
  );
}

function NewHero() {
  return (
    <>
      {/* Mobile layout: asset on top, divider, then text */}
      <div className="md:hidden flex flex-col -mt-24 pt-[121px]">
        {/* Animation area — the SheetBackground behind shows through */}
        <div className="relative h-[280px]" />
        {/* Horizontal divider */}
        <div className="w-full h-px bg-black/20" />
        {/* Text content — blurred background for legibility */}
        <div className="px-6 py-10 relative">
          <div className="absolute inset-0 backdrop-blur-sm bg-[#FFFDF0]/40" />
          <div className="relative z-10">
            <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/60 mb-6">
              <DecodeText text="A NEW FRONTIER FOR REASONING" delay={0} />
            </div>
            <h1 className="font-arizona-mix max-w-md text-[42px] leading-[1.1] tracking-[-1.5] text-[#0a0a0a] text-pretty">
              Neural + Symbolic
            </h1>
            <div className="font-arizona-sans mt-6 max-w-xl text-[17px] leading-[1.6] text-[#0a0a0a]/75 space-y-4">
              <p>
                Symbolica is an artificial intelligence research laboratory
                pioneering the application of category theory and type theory to
                design architectures that are capable of structured reasoning.
              </p>
            </div>
            <div className="mt-8 flex flex-row flex-wrap gap-4">
              <Link
                href="#our-approach"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector("#our-approach")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <PillButton variant="outline" icon="none">
                  Our Thesis
                </PillButton>
              </Link>
              <Link href="/careers">
                <PillButton variant="outline">
                  <span className="min-[390px]:hidden">Join</span>
                  <span className="hidden min-[390px]:inline">
                    Work With
                  </span>{" "}
                  Us
                </PillButton>
              </Link>
            </div>
          </div>
        </div>
        {/* Bottom divider */}
        <div className="w-full h-px bg-black/20" />
      </div>

      {/* Desktop layout: side-by-side grid */}
      <div className="hidden md:grid h-[calc(100vh-80px)] min-h-[500px] max-h-[960px] relative w-full grid-cols-[75%_25%] lg:grid-cols-[50%_50%] grid-rows-[25%_75%] -mt-10">
        {/* L */}
        <div
          className="relative flex flex-col row-start-1 row-span-2 items-start px-24 py-20"
          style={{ paddingTop: "calc(5rem + 30px)" }}
        >
          {/* Blur BG */}
          <div className="-left-1000 absolute bottom-0 -top-100 right-0 backdrop-blur-sm bg-[#FFFDF0]/30" />
          {/* Content */}
          <div className="flex flex-col items-start z-10 justify-end h-full">
            <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/60 mb-6">
              <DecodeText text="A NEW FRONTIER FOR REASONING" delay={0} />
            </div>
            <h1 className="font-arizona-mix max-w-md text-[42px] leading-[1.1] tracking-[-1.5] text-[#0a0a0a] lg:text-[50px] text-pretty">
              Neural + Symbolic
            </h1>
            <div className="font-arizona-sans mt-6 max-w-xl text-[17px] leading-[1.6] text-[#0a0a0a]/75 space-y-4">
              <p>
                Symbolica is an artificial intelligence research laboratory
                pioneering the application of category theory and type theory to
                design architectures that are capable of structured reasoning.
              </p>
            </div>
            <div className="mb-8 mt-8 lg:mt-10 lg:mb-16 flex flex-row flex-wrap gap-4">
              <Link
                href="#our-approach"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector("#our-approach")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                <PillButton variant="outline" icon="none">
                  Our Thesis
                </PillButton>
              </Link>
              <Link href="/careers">
                <PillButton variant="outline">Work With Us</PillButton>
              </Link>
            </div>
          </div>
          {/* R Border */}
          <div
            className="absolute bottom-0 right-0 w-px bg-gradient-to-t from-black/20 to-transparent"
            style={{ top: "-200px" }}
          />
        </div>
        <div className="absolute -left-10000 -right-10000 bottom-0 h-px bg-black/20" />
      </div>
    </>
  );
}

function HeroDescription({ className }: { className: string }) {
  return (
    <div
      className={cn(
        "w-full h-full p-8 pt-12 pb-0 px-16 flex flex-col gap-8 z-20 lg:flex-row items-start font-arizona-sans text-[15px] leading-5 text-white/90",
        className
      )}
    >
      <div>
        Contrastive learning objectives maximize mutual information I(x;z)
        between augmented views through InfoNCE loss bounds, where negative
        sampling strategies determine the tightness of variational estimators
        and downstream transfer performance.
      </div>
      <div>
        Graph neural networks propagate node embeddings via message passing
        where permutation-invariant aggregation functions preserve graph
        isomorphism properties through neighborhood feature pooling operations.
      </div>
    </div>
  );
}

function LightGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#FFFDF0]" />
      <div className="absolute inset-0 opacity-[0.1]">
        <GridOverlay opacity={0.1} lineTone="rgba(0,0,0,0.08)" size={72} />
      </div>
    </div>
  );
}

function TopDarkGrid() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#050505]" />
      <div className="absolute inset-0 opacity-[0.1]">
        <GridOverlay
          opacity={0.1}
          lineTone="rgba(255,255,255,0.12)"
          size={72}
        />
      </div>
    </div>
  );
}

function GridOverlay({
  opacity = 0.2,
  lineTone = "rgba(255,255,255,0.08)",
  size = 64,
}: {
  opacity?: number;
  lineTone?: string;
  size?: number;
}) {
  return (
    <div
      className="size-full"
      style={{
        backgroundImage: `linear-gradient(${lineTone} 1px, transparent 1px), linear-gradient(90deg, ${lineTone} 1px, transparent 1px)`,
        backgroundSize: `${size}px ${size}px`,
        opacity,
      }}
    />
  );
}

function BeyondPatternMatchingSection() {
  const firstParagraph = `Our research program is focused on designing a fully general-purpose program synthesis engine, from the ground up, on our proprietary non-LLM categorical architecture.`;
  const secondParagraph = `We believe that future progress in artificial intelligence depends on the unification of the reliability of symbolic program execution with the adaptability of neural optimization. The way we build this bridge is by leveraging the language of category theory.`;

  return (
    <section
      id="discover"
      className="relative bg-[#FFFDF0] border-b border-black/20 scroll-mt-20"
    >
      <div className="mx-auto max-w-[96rem] px-8 py-32 md:py-48 lg:px-24">
        <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/60 mb-10">
          <DecodeText text="RESEARCH PROGRAM" delay={200} />
        </div>
        <ScrollRevealText
          paragraphs={[firstParagraph, secondParagraph]}
          className="max-w-4xl"
        />
      </div>
    </section>
  );
}

function OurThesisSection() {
  return (
    <>
      {/* Header section */}
      <section
        id="our-approach"
        className="relative bg-[#FFFDF0] scroll-mt-20 border-b border-black/20"
      >
        <div className="mx-auto max-w-[96rem] px-8 pt-40 pb-16 lg:px-24">
          <ContentLockup
            label="Foundational research"
            title="Program Synthesis"
            decodeLabel
            description="At Symbolica, we believe that the narrow domain most likely to yield generalized reasoning capability is program synthesis and theorem proving. A general purpose program synthesis engine or, alternatively, a sufficiently advanced theorem prover, can be configured to perform many different kinds of combinatorial optimization problems. A programmable symbolic engine capable of producing general solutions to these problems would have industry redefining consequences at almost every level."
            light
          />
        </div>
      </section>
      {/* Expanding Frames - separate section for sticky to work */}
      <div className="relative bg-[#FFFDF0] border-b border-black/20">
        <ExpandingFrames />
      </div>
    </>
  );
}

function OurTeamSection() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry?.isIntersecting ?? false);
      },
      { rootMargin: "100px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative bg-[#F5F3E7] mb-[-6rem] md:mb-[-9rem] overflow-hidden"
    >
      <div className="relative z-10 mx-auto max-w-[96rem] px-8 pt-32 pb-40 md:pt-40 md:pb-48 lg:px-24">
        <div className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between">
          {/* Left side - Text Content */}
          <div className="relative flex-1 lg:pr-16">
            {/* Vertical separator line on the right */}
            <div
              className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-black/[0.07]"
              style={{ top: "-10rem", bottom: "-12rem" }}
            />

            {/* Content */}
            <div className="relative z-10">
              <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/60">
                <DecodeText text="THE PATH FORWARD" delay={200} />
              </div>
              <h2 className="font-arizona-mix mt-4 max-w-2xl text-[42px] leading-[1.1] tracking-[-1.5] text-[#0a0a0a] lg:text-[50px] text-pretty">
                General Program Synthesis
              </h2>
              {/* Featured text block - Arizona Mix style */}
              <p className="font-arizona-mix mt-6 max-w-xl text-[22px] leading-[1.4] tracking-[-0.5px] text-[#0a0a0a]">
                We have not yet solved general purpose program synthesis, and
                success is not guaranteed. This challenge remains one of the
                most difficult open research problems in the field of artificial
                intelligence today.
              </p>
              {/* Regular description - Arizona Sans */}
              <p className="font-arizona-sans mt-6 max-w-xl text-[16px] leading-6 text-[#0a0a0a]">
                We have been hard at work leveraging state of the art research
                and engineering techniques to build pieces of the solution
                incrementally. To see our first steps in this direction, try
                Agentica.
              </p>
              {/* CTA */}
              <div className="mt-10">
                <Link href="/agentica">
                  <PillButton variant="outline">Try Agentica</PillButton>
                </Link>
              </div>
            </div>
          </div>

          {/* Right side - empty space for animation to show through */}
          <div className="hidden lg:block w-[520px] flex-shrink-0" />
        </div>
      </div>
    </section>
  );
}
